<script>
import { computed, ref } from 'vue';
import { mapGetters } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import { useConversationLabels } from 'dashboard/composables/useConversationLabels';
import { useContactMetadata } from 'dashboard/composables/useContactMetadata';
import { useContactAttributes } from 'dashboard/composables/useContactAttributes';
import { useConversationAgent } from 'dashboard/composables/useConversationAgent';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import SocialIcons from './SocialIcons.vue';
import EditContact from './EditContact.vue';
import ContactsCard from 'dashboard/components-next/Contacts/ContactsCard/ContactsCard.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CustomAttributesWrapper from './CustomAttributesWrapper.vue';
import LabelDropdown from 'shared/components/ui/label/LabelDropdown.vue';
import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import ContactInfoDropdown from 'dashboard/components/ContactInfoDropdown.vue';
import ContactInfoItem from 'dashboard/components/ContactInfoItem.vue';
import ContactInfoMacro from 'dashboard/components/ContactInfoMacro.vue';
import CarrinhoInfoModal from 'dashboard/components/CarrinhoInfoModal.vue';
import { CONVERSATION_PRIORITY } from 'shared/constants/messages';
import wootConstants from 'dashboard/constants/globals';
import { formatFlagDisplayText } from 'shared/helpers/flagEmoji';

import {
  isAConversationRoute,
  isAInboxViewRoute,
  getConversationDashboardRoute,
} from '../../../../helper/routeHelpers';

export default {
  components: {
    Button,
    EditContact,
    ContactsCard,
    Thumbnail,
    SocialIcons,
    CustomAttributesWrapper,
    LabelDropdown,
    WootDropdownItem,
    WootDropdownMenu,
    DropdownMenu,
    ContactInfoDropdown,
    ContactInfoItem,
    ContactInfoMacro,
    CarrinhoInfoModal,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
    inboxId: {
      type: [Number, String],
      default: undefined,
    },
    channelType: {
      type: String,
      default: '',
    },
  },
  emits: ['panelClose'],
  setup(props) {
    const { isAdmin } = useAdmin();
    const { agentsList } = useAgentsList();
    const {
      savedLabels,
      activeLabels,
      accountLabels,
      addLabelToConversation,
      removeLabelFromConversation,
    } = useConversationLabels();

    // Get current chat from store
    const currentChat = useMapGetter('getSelectedChat');

    // Create reactive references for props
    const contact = computed(() => props.contact);
    const inboxId = computed(() => props.inboxId);

    // Use our new composables
    const contactMetadata = useContactMetadata(contact);
    const contactAttributes = useContactAttributes(contact);
    const conversationAgent = useConversationAgent(inboxId, currentChat);

    return {
      isAdmin,
      agentsList,
      savedLabels,
      activeLabels,
      accountLabels,
      addLabelToConversation,
      removeLabelFromConversation,
      ...contactMetadata,
      ...contactAttributes,
      ...conversationAgent,
    };
  },
  data() {
    return {
      showEditModal: false,
      showContactCardModal: false,
      showDeleteModal: false,
      showAdditionalInfo: true,
      showCustomAttributes: true,
      showConversationActions: true,
      showAgentDropdown: false,
      showTeamDropdown: false,
      showPriorityDropdown: false,
      showContactLabelsDropdown: false,
      showHeaderAgentDropdown: false,
      showHeaderTeamDropdown: false,
      showHeaderPriorityDropdown: false,
      showCarrinhoDropdown: false,
      showCarrinhoModal: false,
      showActionsDropdown: false,
      priorityOptions: [
        {
          id: null,
          name: 'Nenhuma',
          thumbnail: `/assets/images/dashboard/priority/none.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.URGENT,
          name: 'Urgente',
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.URGENT}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.HIGH,
          name: 'Alta',
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.HIGH}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.MEDIUM,
          name: 'Média',
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.MEDIUM}.svg`,
        },
        {
          id: CONVERSATION_PRIORITY.LOW,
          name: 'Baixa',
          thumbnail: `/assets/images/dashboard/priority/${CONVERSATION_PRIORITY.LOW}.svg`,
        },
      ],
    };
  },
  watch: {
    'contact.id': function (newId, oldId) {
      if (newId && newId !== oldId) {
        this.fetchContactData(newId);
      }
    },
    'contact.custom_attributes': {
      handler() {
        // Update when custom attributes change
      },
      deep: true,
    },
  },
  mounted() {
    if (this.contact?.id) {
      this.fetchContactData(this.contact.id);
    } else {
      // If no contact, still fetch labels
      this.$store.dispatch('labels/get');
    }

    // Add ESC key listener for modal
    document.addEventListener('keydown', this.handleEscKey);
  },
  beforeUnmount() {
    // Remove ESC key listener
    document.removeEventListener('keydown', this.handleEscKey);
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      getContactLabels: 'contactLabels/getContactLabels',
      getAttributesByModel: 'attributes/getAttributesByModel',
      currentChat: 'getSelectedChat',
      allLabels: 'labels/getLabels',
      currentUser: 'getCurrentUser',
      teams: 'teams/getTeams',
      conversationUiFlags: 'conversationLabels/getUIFlags',
    }),
    hasAnAssignedTeam() {
      return !!this.currentChat?.meta?.team;
    },
    teamsList() {
      if (this.hasAnAssignedTeam) {
        return [
          { id: 0, name: this.$t('TEAMS_SETTINGS.LIST.NONE') },
          ...this.teams,
        ];
      }
      return this.teams;
    },
    assignedAgent: {
      get() {
        return this.currentChat?.meta?.assignee;
      },
      set(agent) {
        const agentId = agent ? agent.id : 0;
        this.$store
          .dispatch('assignAgent', {
            conversationId: this.currentChat.id,
            agentId,
          })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
          });
      },
    },
    assignedTeam: {
      get() {
        return this.currentChat?.meta?.team;
      },
      set(team) {
        const conversationId = this.currentChat.id;
        const teamId = team ? team.id : 0;
        this.$store
          .dispatch('assignTeam', { conversationId, teamId })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_TEAM'));
          });
      },
    },
    assignedPriority: {
      get() {
        const selectedOption = this.priorityOptions.find(
          opt => opt.id === this.currentChat?.priority
        );

        return selectedOption || this.priorityOptions[0];
      },
      set(priorityItem) {
        const conversationId = this.currentChat.id;
        const priority = priorityItem ? priorityItem.id : null;

        this.$store
          .dispatch('assignPriority', { conversationId, priority })
          .then(() => {
            useAlert(
              this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
                priority: priorityItem.name,
                conversationId,
              })
            );
          });
      },
    },
    showSelfAssign() {
      if (!this.assignedAgent) {
        return true;
      }
      if (this.assignedAgent.id !== this.currentUser.id) {
        return true;
      }
      return false;
    },
    contactLabels() {
      return this.getContactLabels(this.contact?.id) || [];
    },
    hasAtendimentoLabel() {
      return this.currentChat?.labels?.includes('atendimento-humano') || false;
    },
    hasLabel() {
      return labelName => {
        return this.contactLabels.includes(labelName);
      };
    },
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },

    // Delete Modal
    confirmDeleteMessage() {
      return `${this.contact.name}`;
    },
    isConversationOpen() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.OPEN;
    },
    isConversationResolved() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.RESOLVED;
    },
    isConversationPending() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.PENDING;
    },
    isConversationSnoozed() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.SNOOZED;
    },
    labelMenuItems() {
      return this.accountLabels
        ?.map(label => ({
          label: label.title,
          value: label.id,
          thumbnail: { name: label.title, color: label.color },
          isSelected: this.savedLabels.some(
            savedLabel => savedLabel === label.title
          ),
          action: 'conversationLabel',
        }))
        .toSorted((a, b) => Number(a.isSelected) - Number(b.isSelected));
    },
    hasCarrinhoAttribute() {
      if (!this.contact?.custom_attributes) return false;
      
      const hasCarrinhoValue = this.contact.custom_attributes.hasOwnProperty('carrinho') || 
                              this.contact.custom_attributes.hasOwnProperty('Carrinho');
      
      if (!hasCarrinhoValue) return false;
      
      // Check if any defined carrinho/Carrinho attributes are text type
      const attributes = this.getAttributesByModel('contact_attribute') || [];
      const carrinhoAttr = attributes.find(attr => 
        (attr.attribute_key === 'carrinho' || attr.attribute_key === 'Carrinho') &&
        attr.attribute_display_type === 'text'
      );
      
      // If we found a defined text type carrinho attribute, show it
      if (carrinhoAttr) return true;
      
      // If no defined attribute found, assume custom attributes are text type by default
      // and only show for carrinho/Carrinho (since undefined attributes default to text)
      return hasCarrinhoValue;
    },
    carrinhoValue() {
      if (!this.contact?.custom_attributes) return '';
      return this.contact.custom_attributes.carrinho || this.contact.custom_attributes.Carrinho || '';
    },
    parsedCartItems() {
      if (!this.carrinhoValue) return [];
      
      const items = [];
      const parts = this.carrinhoValue.split(',');
      
      // Process pairs of product name and quantity
      for (let i = 0; i < parts.length; i += 2) {
        const productName = parts[i]?.trim();
        const quantity = parts[i + 1]?.trim();
        
        if (productName && quantity) {
          items.push({
            name: productName,
            quantity: parseInt(quantity, 10) || 0
          });
        }
      }
      
      return items;
    },
    isWhatsAppGroup() {
      return this.contact?.identifier?.endsWith('@g.us') || false;
    },
    displayContactName() {
      return this.contact.name?.replace(/\(GROUP\)/gi, '').trim() || this.contact.name;
    },
    displayContactNameHtml() {
      return formatFlagDisplayText(this.displayContactName || '');
    },
  },
  methods: {
    async fetchContactData(contactId) {
      try {
        // Use Promise.all for parallel requests
        await Promise.all([
          this.$store.dispatch('contactLabels/get', contactId),
          this.$store.dispatch('attributes/get', 0),
          this.$store.dispatch('labels/get'),
        ]);
      } catch (error) {
        console.error('Error fetching contact data:', error);
      }
    },
    getPriorityName(priority) {
      if (!priority) return 'Nenhuma';

      const priorityMap = {
        urgent: 'Urgente',
        high: 'Alta',
        medium: 'Média',
        low: 'Baixa',
      };

      return (
        priorityMap[priority] || 'Nenhuma'
      );
    },
    onSelfAssign() {
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      this.assignedAgent = selfAssign;
    },
    toggleAgentDropdown() {
      this.showAgentDropdown = !this.showAgentDropdown;
      this.showTeamDropdown = false;
      this.showPriorityDropdown = false;
      this.showCarrinhoDropdown = false;
    },

    toggleTeamDropdown() {
      this.showTeamDropdown = !this.showTeamDropdown;
      this.showAgentDropdown = false;
      this.showPriorityDropdown = false;
      this.showCarrinhoDropdown = false;
    },

    togglePriorityDropdown() {
      this.showPriorityDropdown = !this.showPriorityDropdown;
      this.showAgentDropdown = false;
      this.showTeamDropdown = false;
      this.showSearchDropdownLabel = false;
      this.showCarrinhoDropdown = false;
    },

    toggleCarrinhoDropdown() {
      this.showCarrinhoDropdown = !this.showCarrinhoDropdown;
      this.showAgentDropdown = false;
      this.showTeamDropdown = false;
      this.showPriorityDropdown = false;
      this.showContactLabelsDropdown = false;
    },

    toggleCarrinhoModal() {
      this.showCarrinhoModal = !this.showCarrinhoModal;
    },

    closeCarrinhoModal() {
      this.showCarrinhoModal = false;
    },

    async handleCarrinhoDelete() {
      if (!this.contact?.id) return;
      
      try {
        // Remove carrinho from custom attributes
        const updatedAttributes = { ...this.contact.custom_attributes };
        delete updatedAttributes.carrinho;
        delete updatedAttributes.Carrinho;
        
        await this.$store.dispatch('contacts/update', {
          id: this.contact.id,
          custom_attributes: updatedAttributes,
        });
        
        useAlert('Carrinho excluído com sucesso');
        this.closeCarrinhoModal();
      } catch (error) {
        const errorMessage = error?.response?.message || 'Erro ao excluir o carrinho';
        useAlert(errorMessage, 'error');
      }
    },

    handleCarrinhoExport(exportData) {
      // Additional handling for export if needed
      console.log('Carrinho exportado:', exportData);
    },

    handleLabelAction({ value }) {
      const selectedLabel = this.accountLabels.find(label => label.id === value);
      if (!selectedLabel) return;

      if (this.savedLabels.includes(selectedLabel.title)) {
        this.removeLabelFromConversation(selectedLabel.title);
      } else {
        // addLabelToConversation expects the full label object
        this.addLabelToConversation(selectedLabel);
      }
      this.showContactLabelsDropdown = false;
    },

    toggleContactLabels() {
      this.showContactLabelsDropdown = !this.showContactLabelsDropdown;
      this.showAgentDropdown = false;
      this.showTeamDropdown = false;
      this.showPriorityDropdown = false;
      this.showSearchDropdownLabel = false;
      this.showCarrinhoDropdown = false;
    },

    closeContactLabelsDropdown() {
      this.showContactLabelsDropdown = false;
    },

    onAgentSelect(selectedItem) {
      if (this.assignedAgent && this.assignedAgent.id === selectedItem.id) {
        this.assignedAgent = null;
      } else {
        this.assignedAgent = selectedItem;
      }
      this.showAgentDropdown = false;
    },

    onTeamSelect(selectedItemTeam) {
      if (this.assignedTeam && this.assignedTeam.id === selectedItemTeam.id) {
        this.assignedTeam = null;
      } else {
        this.assignedTeam = selectedItemTeam;
      }
      this.showTeamDropdown = false;
    },

    onPrioritySelect(selectedPriorityItem) {
      const isSamePriority =
        this.assignedPriority &&
        this.assignedPriority.id === selectedPriorityItem.id;

      this.assignedPriority = isSamePriority ? null : selectedPriorityItem;
      this.showPriorityDropdown = false;
    },


    getLabelColor(labelName) {
      if (!this.allLabels || !labelName) return null;

      const label = this.allLabels.find(l => l.title === labelName);
      return label ? label.color : null;
    },

    getLabelStyle(labelName) {
      const color = this.getLabelColor(labelName);
      if (!color) return { backgroundColor: '#E0E6ED' };

      // Create a light background color based on the label color
      return {
        backgroundColor: `${color}20`, // 20 is hex for 12% opacity
        color: color,
        borderColor: `${color}40`, // 40 is hex for 25% opacity
      };
    },
    checkLabel(labelName) {
      return this.contactLabels.includes(labelName);
    },
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    toggleContactCardModal() {
      this.showContactCardModal = !this.showContactCardModal;
    },
    toggleDeleteModal() {
      this.showDeleteModal = !this.showDeleteModal;
    },
    confirmDeletion() {
      this.deleteContact(this.contact);
      this.closeDelete();
    },
    closeDelete() {
      this.showDeleteModal = false;
      this.showEditModal = false;
    },
    async handleCopyToClipboard(text) {
      if (!text) return;
      const success = await this.copyToClipboard(text);
      if (success) {
        useAlert('Copiado com sucesso');
      } else {
        useAlert('Erro ao copiar o texto', 'error');
      }
    },



    async handleContactCardUpdate(updatedContactData) {
      try {
        await this.$store.dispatch('contacts/update', updatedContactData);
        useAlert('Contato atualizado com sucesso');
        this.toggleContactCardModal();
      } catch (error) {
        const errorMessage = error?.response?.message || 'Erro ao atualizar o contato';
        useAlert(errorMessage, 'error');
      }
    },
    handleEscKey(event) {
      if (event.key === 'Escape') {
        if (this.showContactCardModal) {
          this.toggleContactCardModal();
        } else if (this.showCarrinhoModal) {
          this.closeCarrinhoModal();
        }
      }
    },
    toggleHeaderAgentDropdown() {
      this.showHeaderAgentDropdown = !this.showHeaderAgentDropdown;
      this.showHeaderTeamDropdown = false;
      this.showHeaderPriorityDropdown = false;
    },
    closeHeaderAgentDropdown() {
      this.showHeaderAgentDropdown = false;
    },
    toggleHeaderTeamDropdown() {
      this.showHeaderTeamDropdown = !this.showHeaderTeamDropdown;
      this.showHeaderAgentDropdown = false;
      this.showHeaderPriorityDropdown = false;
    },
    closeHeaderTeamDropdown() {
      this.showHeaderTeamDropdown = false;
    },
    toggleHeaderPriorityDropdown() {
      this.showHeaderPriorityDropdown = !this.showHeaderPriorityDropdown;
      this.showHeaderAgentDropdown = false;
      this.showHeaderTeamDropdown = false;
    },
    closeHeaderPriorityDropdown() {
      this.showHeaderPriorityDropdown = false;
    },
    toggleActionsDropdown() {
      this.showActionsDropdown = !this.showActionsDropdown;
    },
    closeActionsDropdown() {
      this.showActionsDropdown = false;
    },
    openSnoozeModal() {
      const ninja = document.querySelector('ninja-keys');
      ninja.open({ parent: 'snooze_conversation' });
    },
    toggleConversationStatus(status) {
      this.loading = true;
      this.$store
        .dispatch('toggleStatus', {
          conversationId: this.currentChat.id,
          status,
        })
        .then(() => {
          useAlert(this.$t('CONVERSATION.CHANGE_STATUS'));
          this.loading = false;
        });
    },
    resolveConversation() {
      this.toggleConversationStatus(wootConstants.STATUS_TYPE.RESOLVED);
    },
    reopenConversation() {
      this.toggleConversationStatus(wootConstants.STATUS_TYPE.OPEN);
    },
    markPending() {
      this.toggleConversationStatus(wootConstants.STATUS_TYPE.PENDING);
    },
    onClickViewDetails() {
      this.$router.push(this.contactProfileLink);
    },
    async deleteContact({ id }) {
      try {
        await this.$store.dispatch('contacts/delete', id);
        this.$emit('panelClose');
        useAlert(this.$t('DELETE_CONTACT.API.SUCCESS_MESSAGE'));

        if (isAConversationRoute(this.$route.name)) {
          this.$router.push({
            name: getConversationDashboardRoute(this.$route.name),
          });
        } else if (isAInboxViewRoute(this.$route.name)) {
          this.$router.push({
            name: 'inbox_view',
          });
        } else if (this.$route.name !== 'contacts_dashboard') {
          this.$router.push({
            name: 'contacts_dashboard',
          });
        }
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('DELETE_CONTACT.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <!-- ContactInfo -->
  <div class="bg-n-solid-1">
    <!-- Action Buttons -->
    <div class="flex gap-2 w-full mb-4 sticky top-0 z-10 bg-n-solid-1 p-3">
      <!-- Edit Button -->
      <Button
        v-tooltip="'Editar contato'"
        label="Editar"
        size="sm"
        color="slate"
        class="flex-1"
        icon="i-lucide-edit"
        :disabled="loading"
        @click="toggleContactCardModal"
      />

      <!-- Status Actions Dropdown -->
      <div class="flex-[1.2] relative">
        <div class="rounded-lg button-group outline-1 outline outline-n-container w-full">
          <Button
            label="Ações"
            size="sm"
            color="slate"
            class="ltr:rounded-r-none rtl:rounded-l-none !outline-0 flex-1"
            :disabled="loading"
            @click="toggleActionsDropdown"
          />
          <Button
            icon="i-lucide-chevron-down"
            :disabled="loading"
            size="sm"
            class="ltr:rounded-l-none rtl:rounded-r-none !outline-0"
            color="slate"
            trailing-icon
            @click="toggleActionsDropdown"
          />
        </div>
        
        <!-- Dropdown Menu -->
        <div
          v-if="showActionsDropdown"
          v-on-clickaway="closeActionsDropdown"
          class="dropdown-pane dropdown-pane--open absolute top-full right-0 mt-1 z-50 min-w-[180px]"
        >
          <WootDropdownMenu class="mb-0">
            <WootDropdownItem v-if="isConversationOpen">
              <Button
                :label="$t('CONVERSATION.HEADER.RESOLVE_ACTION')"
                ghost
                slate
                sm
                start
                icon="i-lucide-check"
                class="w-full"
                @click="resolveConversation(); closeActionsDropdown()"
              />
            </WootDropdownItem>
            <WootDropdownItem v-if="isConversationResolved">
              <Button
                :label="$t('CONVERSATION.HEADER.REOPEN_ACTION')"
                ghost
                slate
                sm
                start
                icon="i-lucide-rotate-ccw"
                class="w-full"
                @click="reopenConversation(); closeActionsDropdown()"
              />
            </WootDropdownItem>
            <WootDropdownItem v-if="isConversationPending || isConversationSnoozed">
              <Button
                :label="$t('CONVERSATION.HEADER.OPEN_ACTION')"
                ghost
                slate
                sm
                start
                icon="i-lucide-folder-open"
                class="w-full"
                @click="reopenConversation(); closeActionsDropdown()"
              />
            </WootDropdownItem>
            <WootDropdownItem v-if="!isConversationPending">
              <Button
                :label="$t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL')"
                ghost
                slate
                sm
                start
                icon="i-lucide-alarm-clock-minus"
                class="w-full"
                @click="openSnoozeModal(); closeActionsDropdown()"
              />
            </WootDropdownItem>
            <WootDropdownItem v-if="!isConversationPending">
              <Button
                :label="$t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING')"
                ghost
                slate
                sm
                start
                icon="i-lucide-circle-dot-dashed"
                class="w-full"
                @click="markPending(); closeActionsDropdown()"
              />
            </WootDropdownItem>
          </WootDropdownMenu>
        </div>
      </div>
      
      <!-- Delete Button -->
      <Button
        v-if="isAdmin"
        v-tooltip="'Excluir contato'"
        label="Excluir"
        size="sm"
        color="slate"
        class="flex-1 hover:!bg-red-300 dark:hover:!bg-red-800"
        icon="i-lucide-trash-2"
        :disabled="loading || uiFlags.isDeleting"
        @click="toggleDeleteModal"
      />
    </div>

    <!-- Card Header -->
    <div
      class="flex flex-col pt-3 p-4 bg-n-solid-1"
    >
      <div class="flex flex-col items-center text-center">
        <div v-if="showAvatar" class="mb-3">
          <Thumbnail
            :src="contact.thumbnail"
            size="80px"
            :username="contact.name"
            :status="contact.availability_status"
            :fallback-text="contactInitials"
          />
        </div>
        <div>
          <h2
            class="text-lg font-semibold text-slate-800 dark:text-slate-100 m-0 break-words flex items-center justify-center gap-2"
          >
            <span v-dompurify-html="displayContactNameHtml"></span>
            <span
              v-if="assignedPriority?.id"
              class="w-4 h-4 flex items-center justify-center rounded-full"
              :class="{
                'bg-red-500': assignedPriority.id === 'urgent',
                'bg-orange-500': assignedPriority.id === 'high',
                'bg-yellow-500': assignedPriority.id === 'medium',
                'bg-grass-500': assignedPriority.id === 'low',
              }"
            >
              <span class="text-white text-xs font-bold leading-none">!</span>
            </span>
          </h2>
          <div 
            v-if="!isWhatsAppGroup && contact.phone_number"
            v-tooltip="'Clique para copiar'"
            class="text-sm text-slate-500 dark:text-slate-400 m-0 cursor-pointer hover:bg-n-slate-3 dark:hover:bg-n-slate-5 hover:underline py-1 rounded transition-colors inline-block"
            @click="handleCopyToClipboard(contact.phone_number)"
          >
            {{ contact.phone_number }}
          </div>
          <div 
            v-else-if="!isWhatsAppGroup"
            class="text-sm text-slate-500 dark:text-slate-400 m-0"
          >
            Sem telefone
          </div>
          
          <!-- Conversation Labels -->
          <div class="relative mt-2">
            <div class="text-xs text-slate-500 dark:text-slate-400 mb-2 text-center">
              {{ isWhatsAppGroup ? 'Marcadores do grupo' : 'Marcadores do contato' }}
            </div>
            <div class="flex flex-wrap gap-1 mt-1 justify-center">
              <TransitionGroup
                name="label"
                tag="div"
                class="flex flex-wrap gap-1 justify-center"
              >
                <span
                  v-for="label in currentChat?.labels || []"
                  :key="label"
                  v-tooltip="'Remover marcador'"
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium max-w-full cursor-pointer hover:brightness-75 transition-all duration-200"
                  :style="getLabelStyle(label)"
                  @click="removeLabelFromConversation(label)"
                >
                  <span
                    v-if="getLabelColor(label)"
                    class="h-2 w-2 rounded-full mr-1"
                    :style="{ backgroundColor: getLabelColor(label) }"
                  />
                  <span class="truncate">{{ label }}</span>
                </span>
              </TransitionGroup>
              
              <!-- Add Label Button -->
              <div class="relative flex items-center">
                <button
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border-none bg-n-slate-3 outline-none hover:bg-n-slate-4 transition-colors"
                  @click="toggleContactLabels"
                >
                  <span class="i-lucide-plus" />
                  Adicionar
                </button>
                <DropdownMenu
                  v-if="showContactLabelsDropdown"
                  v-on-clickaway="closeContactLabelsDropdown"
                  :menu-items="labelMenuItems"
                  show-search
                  class="z-[100] w-48 mt-2 ltr:right-0 rtl:left-0 top-full max-h-52 overflow-y-auto"
                  @action="handleLabelAction"
                >
                  <template #thumbnail="{ item }">
                    <div
                      class="rounded-lg size-2"
                      :style="{ backgroundColor: item.thumbnail.color }"
                    />
                  </template>
                </DropdownMenu>
              </div>
            </div>
          </div>
          
        </div>
      </div>

      <!-- Assume Conversation Button -->
      <div v-if="!isWhatsAppGroup" class="flex items-center mt-3 w-full">
        <button
          type="button"
          class="flex-grow inline-flex items-center justify-center gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 py-2.5 px-4 text-sm font-medium"
          :class="
            !hasIaAgent || hasAtendimentoLabel
              ? 'text-n-blue-text outline-n-brand bg-transparent hover:enabled:bg-n-brand/10 focus-visible:bg-n-brand/10'
              : 'bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110 outline-transparent'
          "
          :disabled="loading || !hasIaAgent"
          @click="handleAssumeConversation($route, $store)"
        >
          <span>{{
            !hasIaAgent
              ? 'Nenhum agente IA'
              : hasAtendimentoLabel
                ? 'Retomar atendimento IA'
                : 'Assumir conversa'
          }}</span>
        </button>
      </div>


    </div>
      <!-- Info Sections Wrapper -->
      <div class="px-4 bg-n-solid-1">
        <!-- Conversation Actions -->
        <div class="transition-colors">
          <!-- Content -->
          <div class="overflow-visible transition-all duration-200">
            <!-- AI Agent -->
            <div class="py-1 px-1 border-b border-n-weak">
              <div class="flex items-center bg-n-solid-1 hover:bg-n-slate-3 transition-colors rounded-lg p-3">
                <div class="w-6 text-center mr-3" :class="hasIaAgent && !hasAtendimentoLabel ? 'text-woot-500' : 'text-slate-500 dark:text-slate-400'">
                  <span class="i-lucide-bot" />
                </div>
                <div class="flex-grow">
                  <div class="text-xs mb-1 text-slate-500 dark:text-slate-400">
                    Agente de IA
                  </div>
                  <div class="text-sm font-medium text-slate-700 dark:text-slate-200">
                    {{ isWhatsAppGroup ? 'Não disponível para grupos' : hasIaAgent ? (iaAgentName || 'Agente IA Ativo') : 'Nenhum' }}{{ !isWhatsAppGroup && hasAtendimentoLabel ? ' (Pausado)' : '' }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Carrinho do cliente -->
            <div v-if="hasCarrinhoAttribute" class="py-1 px-1 border-b border-n-weak">
              <div
                class="flex items-center cursor-pointer bg-n-solid-1 hover:bg-n-slate-3 transition-colors rounded-lg p-3 group" 
                @click="toggleCarrinhoModal"
              >
                <!-- Icon with hover transition -->
                <div class="w-6 h-6 mr-3 relative flex-shrink-0">
                  <span class="i-lucide-shopping-cart absolute inset-0 m-auto opacity-100 transition-opacity duration-200 group-hover:opacity-0 text-slate-500 dark:text-slate-400" />
                  <span class="i-lucide-edit absolute inset-0 m-auto text-n-brand opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
                </div>

                <!-- Title and Value -->
                <div class="flex-grow">
                  <div class="text-xs text-slate-500 dark:text-slate-400 mb-1">
                    Carrinho do cliente
                  </div>
                  <div class="text-sm font-medium text-slate-700 dark:text-slate-200">
                    {{ parsedCartItems.length > 0 ? `${parsedCartItems.length} ${parsedCartItems.length === 1 ? 'item' : 'itens'}` : 'Vazio' }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Assigned Agent -->
            <ContactInfoDropdown
              v-model="showAgentDropdown"
              label="Atendente"
              icon="i-lucide-user"
              :items="agentsList"
              :selected-item="assignedAgent"
              placeholder="Nenhum"
              empty-message="Nenhum agente disponível"
              :show-border="false"
              @select="onAgentSelect"
            />

            <!-- Assigned Team -->
            <ContactInfoDropdown
              v-model="showTeamDropdown"
              label="Equipe"
              icon="i-lucide-users"
              :items="teamsList"
              :selected-item="assignedTeam"
              :placeholder="$t('TEAMS_SETTINGS.LIST.NONE')"
              empty-message="Nenhuma equipe disponível"
              @select="onTeamSelect"
            />

            <!-- Priority -->
            <ContactInfoDropdown
              v-model="showPriorityDropdown"
              label="Prioridade"
              icon="i-lucide-flag"
              :items="priorityOptions"
              :selected-item="assignedPriority"
              :placeholder="getPriorityName(null)"
              @select="onPrioritySelect"
            />

          </div>
        </div>

        <!-- Location -->
        <ContactInfoItem
          v-if="location"
          label="Localização"
          icon="i-lucide-map-pin"
          :value="location"
          :html-content="true"
        />

        <!-- Additional Information -->
        <div v-if="location || contact.email || additionalAttributes.company_name || additionalAttributes.description || hasSocialProfiles" class="transition-colors">
          <!-- Content -->
          <div class="overflow-hidden transition-all duration-200">
            <!-- Email -->
            <ContactInfoItem
              v-if="contact.email"
              label="Email"
              icon="i-lucide-mail"
              :value="contact.email"
            />

            <!-- Social Profiles -->
            <ContactInfoItem
              v-if="hasSocialProfiles"
              label="Redes Sociais"
              icon="i-lucide-share-2"
            >
              <SocialIcons :social-profiles="socialProfiles" />
            </ContactInfoItem>

            <!-- Company -->
            <ContactInfoItem
              v-if="additionalAttributes.company_name"
              label="Empresa"
              icon="i-lucide-building"
              :value="additionalAttributes.company_name"
            />

            <!-- Bio -->
            <ContactInfoItem
              v-if="additionalAttributes.description"
              label="Bio"
              icon="i-lucide-user-circle"
              :value="additionalAttributes.description"
              :multiline="true"
            />
          </div>
        </div>

        <!-- Macros -->
        <ContactInfoMacro
          v-if="currentChat?.id"
          :conversation-id="currentChat.id"
        />

        <!-- Custom Attributes -->
        <div v-if="!isWhatsAppGroup" class="py-1 px-1 border-t border-n-weak">
          <div class="overflow-visible transition-all duration-200">
            <CustomAttributesWrapper
              :contact="contact"
              attribute-type="contact_attribute"
            />
          </div>
        </div>
      </div>
      
    <!-- Modals -->
    <EditContact
      v-if="showEditModal"
      :show="showEditModal"
      :contact="contact"
      @cancel="toggleEditModal"
    />
    
    <!-- ContactsCard Modal -->
    <div
      v-if="showContactCardModal"
      class="fixed inset-0 z-[1000] flex items-center justify-center p-8 bg-n-slate-12/10 backdrop-blur-sm dark:bg-n-slate-1/10 "
      @click.self="toggleContactCardModal"
    >
      <div class="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <ContactsCard
          :id="contact.id"
          :name="contact.name"
          :email="contact.email"
          :phone-number="contact.phone_number"
          :thumbnail="contact.thumbnail"
          :additional-attributes="normalizedAdditionalAttributes"
          :is-expanded="true"
          :is-updating="uiFlags.isUpdating"
          @update-contact="handleContactCardUpdate"
          @show-contact="onClickViewDetails"
        />
      </div>
    </div>
    
    <woot-delete-modal
      v-if="showDeleteModal"
      v-model:show="showDeleteModal"
      :on-close="closeDelete"
      :on-confirm="confirmDeletion"
      :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
      :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
      :message-value="confirmDeleteMessage"
      :message-after="$t('DELETE_CONTACT.CONFIRM.MESSAGE_AFTER')"
      :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
      :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
    />
    
    <!-- Carrinho Info Modal -->
    <CarrinhoInfoModal
      :show="showCarrinhoModal"
      :cart-items="parsedCartItems"
      :cart-value="carrinhoValue"
      :contact="contact"
      @close="closeCarrinhoModal"
      @delete="handleCarrinhoDelete"
      @export="handleCarrinhoExport"
    />
  </div>
</template>

<style scoped>
.dropdown-pane {
  .dropdown-menu__item {
    @apply mb-0;
  }
}

/* Label transition animations */
.label-enter-active,
.label-leave-active {
  transition: all 0.3s ease;
}

.label-enter-from {
  opacity: 0;
  transform: scale(0.8);
}

.label-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

.label-move {
  transition: transform 0.3s ease;
}
</style>
